import { Avatar, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import { CompanyUserDetails } from "../companyUserDetailsTypes"

const useStyles = makeStyles()((theme) => ({
    header: {
        padding: "1.25rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    avatar: {
        width: "2.5rem",
        height: "2.5rem",
        fontSize: ".875rem",
        fontWeight: 600,
        background: theme.palette.tailwind.orange[500],
        color: theme.palette.common.white,
    },
    name: {
        color: theme.palette.tailwind.slate[900],
    },
    email: {
        color: theme.palette.tailwind.slate[500],
    },
    closeButton: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".75rem",
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[600],
    },
}))

function initialsFor(user: CompanyUserDetails): string {
    if (user.displayInitials) return user.displayInitials
    const parts = user.displayName.trim().split(/\s+/)
    const first = parts[0]?.charAt(0) ?? ""
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : ""
    return (first + last).toUpperCase()
}

type CompanyUserDetailsHeaderProps = Readonly<{
    user?: CompanyUserDetails
    isActive: boolean
    onClose: () => void
}>

export function CompanyUserDetailsHeader({ user, isActive, onClose }: CompanyUserDetailsHeaderProps) {
    const { classes } = useStyles()

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing="1rem"
            className={classes.header}
        >
            {user ? (
                <Stack spacing=".5rem" minWidth={0}>
                    <Stack direction="row" alignItems="center" spacing=".75rem" minWidth={0}>
                        <Avatar className={classes.avatar}>{initialsFor(user)}</Avatar>
                        <Stack minWidth={0}>
                            <Typography variant="h5" fontWeight={700} noWrap className={classes.name}>
                                {user.displayName}
                            </Typography>
                            <Typography variant="body2" noWrap className={classes.email}>
                                {user.email}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction="row">
                        <LocalizedStrict
                            id={isActive ? "company-users-status-active" : "company-users-status-inactive"}
                        >
                            <StandardChip variant={isActive ? "success" : "grey"}>
                                {isActive ? "Active" : "Inactive"}
                            </StandardChip>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            ) : (
                <span />
            )}
            <IconButton size="small" aria-label="close" className={classes.closeButton} onClick={onClose}>
                <FontAwesomeIcon icon={faXmark} size="sm" />
            </IconButton>
        </Stack>
    )
}
